import { useEffect, useState } from "react";
import { goBack, pushScreen } from "../backstack";
import { toInputValue } from "../scheduler/dates";

// In-app date & time picker. The native Android/iOS pickers can't show the
// weekday of the chosen date, which is what drivers look for, so this draws a
// month grid with Mon–Sun headers and names the day in full.

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const pad = (n: number) => String(n).padStart(2, "0");
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/** 6×7 grid of dates covering the month, starting on Monday. */
function monthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7; // Monday = 0
  const start = addDays(first, -offset);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

export default function DateTimePicker({
  value,
  onChange,
  onClose,
}: {
  value: Date;
  onChange: (date: Date) => void;
  onClose: () => void;
}) {
  const [day, setDay] = useState(() => new Date(value.getFullYear(), value.getMonth(), value.getDate()));
  const [time, setTime] = useState(() => `${pad(value.getHours())}:${pad(value.getMinutes())}`);
  const [view, setView] = useState({ year: value.getFullYear(), month: value.getMonth() });
  const today = new Date();

  // Phone back button closes the sheet instead of leaving the form.
  useEffect(() => {
    pushScreen(onClose);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shiftMonth = (n: number) => {
    const d = new Date(view.year, view.month + n, 1);
    setView({ year: d.getFullYear(), month: d.getMonth() });
  };
  const pickDay = (d: Date) => {
    setDay(d);
    if (d.getMonth() !== view.month || d.getFullYear() !== view.year) {
      setView({ year: d.getFullYear(), month: d.getMonth() });
    }
  };

  const [hh, mm] = time.split(":").map(Number);
  const timeOk = Number.isInteger(hh) && Number.isInteger(mm) && hh >= 0 && hh < 24 && mm >= 0 && mm < 60;
  const chosen = new Date(day.getFullYear(), day.getMonth(), day.getDate(), timeOk ? hh : 0, timeOk ? mm : 0);

  const confirm = () => {
    if (!timeOk) return;
    onChange(chosen);
    goBack();
  };

  const monthLabel = new Date(view.year, view.month, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const chosenLabel = chosen.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

  const quick = (label: string, d: Date) => (
    <button
      key={label}
      type="button"
      onClick={() => pickDay(d)}
      className={`rounded-full px-3.5 py-1.5 text-xs font-bold shadow-sm ${sameDay(d, day) ? "bg-ink text-white" : "bg-blush-soft"}`}
    >
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 sm:items-center sm:px-6" onClick={goBack}>
      <div
        className="pop-in flex max-h-[92dvh] w-full max-w-md flex-col gap-3 overflow-y-auto rounded-t-[22px] bg-white p-5 shadow-pop sm:rounded-[20px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center">
          <div className="text-[19px] font-bold tracking-[-0.02em]">{chosenLabel}</div>
          <div className="text-sm font-semibold text-ink/50">{chosen.getFullYear()} · {timeOk ? time : "--:--"}</div>
        </div>

        <div className="flex flex-wrap justify-center gap-2">
          {quick("Today", today)}
          {quick("Tomorrow", addDays(today, 1))}
          {quick("In 2 days", addDays(today, 2))}
          {quick("Next week", addDays(today, 7))}
        </div>

        <div className="flex items-center justify-between">
          <button type="button" onClick={() => shiftMonth(-1)} aria-label="Previous month" className="flex size-10 items-center justify-center rounded-full bg-blush font-bold text-rose-ink">
            ‹
          </button>
          <span className="font-bold">{monthLabel}</span>
          <button type="button" onClick={() => shiftMonth(1)} aria-label="Next month" className="flex size-10 items-center justify-center rounded-full bg-blush font-bold text-rose-ink">
            ›
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center">
          {WEEKDAYS.map((w) => (
            <div key={w} className={`text-xs font-bold ${w === "Sat" || w === "Sun" ? "text-hot/70" : "text-ink/50"}`}>
              {w}
            </div>
          ))}
          {monthGrid(view.year, view.month).map((d) => {
            const inMonth = d.getMonth() === view.month;
            const selected = sameDay(d, day);
            const isToday = sameDay(d, today);
            return (
              <button
                key={d.toISOString()}
                type="button"
                onClick={() => pickDay(d)}
                aria-label={d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
                aria-pressed={selected}
                className={`flex h-10 items-center justify-center rounded-full text-sm font-bold transition-colors ${
                  selected
                    ? "bg-rose-grad text-white shadow-button"
                    : isToday
                      ? "bg-lemon"
                      : inMonth
                        ? "hover:bg-page"
                        : "text-ink/30"
                }`}
              >
                {d.getDate()}
              </button>
            );
          })}
        </div>

        <label className="flex items-center justify-between gap-3">
          <span className="text-[12.5px] font-semibold text-[#3d2930]">Time</span>
          <input
            type="time"
            value={time}
            step={300}
            onChange={(e) => setTime(e.target.value)}
            className={`h-12 w-36 rounded-[14px] bg-white px-4 text-center text-lg font-bold outline-none border border-line focus:border-[#e39aa8] focus:ring-[3px] focus:ring-rose/20 ${timeOk ? "" : "ring-2 ring-hot"}`}
          />
        </label>

        <div className="flex gap-2">
          <button type="button" onClick={goBack} className="h-12 flex-1 rounded-[14px] bg-white font-bold border border-line text-[#3a1c25]">
            Cancel
          </button>
          <button type="button" onClick={confirm} disabled={!timeOk} className="h-12 flex-1 rounded-[14px] bg-rose-grad font-bold text-white disabled:opacity-50 shadow-button">
            Set
          </button>
        </div>
      </div>
    </div>
  );
}
